import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import ConfirmModal from '../components/modals/ConfirmModal';
import './PendingKYCPage.css';

type QueueTab = 'pending' | 'manual-review' | 'suspended' | 'aml';

interface KYCQueueRow {
  userId: string;
  email: string;
  username: string;
  kycStatus: string;
  sumsubApplicantId: string | null;
  rejectionReason?: string | null;
  twoPlusTwoMatches?: number | null;
  submittedAt: string | null;
  createdAt: string;
}

interface AmlAlertRow {
  id: string;
  checkId: string;
  status: string;
  userEmail?: string;
  username?: string;
  riskLevel?: string;
  triggerType?: string;
  createdAt: string;
}

interface KYCDetails {
  kycStatus: {
    status: string;
    sumsubApplicantId: string | null;
    submittedAt: string | null;
    approvedAt: string | null;
    approvedBy: string | null;
    rejectionReason: string | null;
    resubmissionRequiredReason: string | null;
  };
  images: {
    faceImageUrl: string | null;
    idFrontImageUrl: string | null;
    idBackImageUrl: string | null;
  };
  submissions: Array<{
    id: string;
    status: string;
    reviewStatus: string | null;
    adminAction: string | null;
    adminNotes: string | null;
    createdAt: string;
  }>;
}

const TABS: Array<{ id: QueueTab; label: string }> = [
  { id: 'pending', label: 'Pending Review' },
  { id: 'manual-review', label: 'Manual Review' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'aml', label: 'AML Alerts' },
];

const TAB_EMPTY_COPY: Record<QueueTab, { title: string; body: string }> = {
  pending: { title: 'No Pending Submissions', body: 'All KYC submissions have been reviewed.' },
  'manual-review': {
    title: 'Manual Review Queue Empty',
    body: 'No users are awaiting a manual KYC decision.',
  },
  suspended: { title: 'No Suspended Users', body: 'No KYC-suspended users right now.' },
  aml: { title: 'No AML Alerts', body: 'No AML alerts are pending review.' },
};

// Every KYC status the backend can hold, with a readable label + chip class.
const KYC_STATUS_META: Record<string, { label: string; className: string }> = {
  none: { label: 'No KYC', className: 'kyc-none' },
  pending: { label: 'Pending', className: 'pending' },
  auto_verified: { label: 'Auto-Verified', className: 'kyc-auto-verified' },
  pending_full_kyc: { label: 'Awaiting Full KYC', className: 'kyc-pending-full' },
  approved: { label: 'Approved', className: 'kyc-approved' },
  rejected: { label: 'Rejected', className: 'kyc-rejected' },
  resubmission_required: { label: 'Resubmission Required', className: 'kyc-resubmission' },
  manual_review: { label: 'Manual Review', className: 'kyc-manual-review' },
  pending_manual_review: { label: 'Pending Manual Review', className: 'kyc-pending-manual' },
  suspended: { label: 'Suspended', className: 'kyc-suspended' },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const meta = KYC_STATUS_META[status] || { label: status || 'Unknown', className: 'kyc-none' };
  return <span className={`status-badge ${meta.className}`}>{meta.label}</span>;
};

/**
 * KYC document image behind admin auth. The backend image endpoints require
 * the bearer token, so the bytes are fetched via the API client and rendered
 * from an object URL (a plain <img src> would 401).
 */
const AuthedKYCImage: React.FC<{ path: string; label: string }> = ({ path, label }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setImgError(null);
    apiService
      .fetchKYCImageBlob(path)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setImgError(err instanceof Error ? err.message : 'Failed to load image');
        }
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path]);

  return (
    <div className="kyc-image-item">
      <label>{label}</label>
      {objectUrl ? (
        <img
          src={objectUrl}
          alt={label}
          className="kyc-review-image"
          onClick={() => window.open(objectUrl, '_blank')}
        />
      ) : (
        <div className={`kyc-image-placeholder${imgError ? ' failed' : ''}`}>
          {imgError ? `Unavailable: ${imgError}` : 'Loading image…'}
        </div>
      )}
    </div>
  );
};

type ReasonAction = 'reject' | 'request-documents' | 'suspend' | 'unsuspend';

const REASON_MODAL_CONFIG: Record<
  ReasonAction,
  {
    title: string;
    label: string;
    placeholder: string;
    required: boolean;
    confirmText: string;
    loadingText: string;
    confirmClass: string;
  }
> = {
  reject: {
    title: 'Reject KYC',
    label: 'Rejection Reason',
    placeholder: 'Please provide a reason for rejection...',
    required: true,
    confirmText: 'Reject',
    loadingText: 'Rejecting...',
    confirmClass: 'btn-danger',
  },
  'request-documents': {
    title: 'Request Documents',
    label: 'Required Documents',
    placeholder: 'Describe which documents the user must resubmit...',
    required: true,
    confirmText: 'Send Request',
    loadingText: 'Sending...',
    confirmClass: 'btn-warning',
  },
  suspend: {
    title: 'Suspend KYC',
    label: 'Suspension Reason',
    placeholder: 'Please provide a reason for the suspension...',
    required: true,
    confirmText: 'Suspend',
    loadingText: 'Suspending...',
    confirmClass: 'btn-danger',
  },
  unsuspend: {
    title: 'Unsuspend KYC',
    label: 'Reason (optional)',
    placeholder: 'Optional note for the audit trail...',
    required: false,
    confirmText: 'Unsuspend',
    loadingText: 'Unsuspending...',
    confirmClass: 'btn-success',
  },
};

type ConfirmAction = 'approve' | 'reset';

const CONFIRM_CONFIG: Record<
  ConfirmAction,
  { title: string; message: string; confirmText: string; buttonClass: 'success' | 'danger' }
> = {
  approve: {
    title: 'Approve KYC',
    message: 'Are you sure you want to approve this KYC submission? The user becomes fully verified.',
    confirmText: 'Approve',
    buttonClass: 'success',
  },
  reset: {
    title: 'Reset KYC',
    message:
      'Reset this user’s KYC so they can submit again from scratch? The current status and decision are cleared.',
    confirmText: 'Reset KYC',
    buttonClass: 'danger',
  },
};

const PendingKYCPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<QueueTab>('pending');
  const [rows, setRows] = useState<KYCQueueRow[]>([]);
  const [amlAlerts, setAmlAlerts] = useState<AmlAlertRow[]>([]);
  const [tabCounts, setTabCounts] = useState<Partial<Record<QueueTab, number>>>({});
  const [suspendedUnavailable, setSuspendedUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail modal
  const [selectedRow, setSelectedRow] = useState<KYCQueueRow | null>(null);
  const [kycDetails, setKycDetails] = useState<KYCDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Actions
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionUserId, setActionUserId] = useState<string | null>(null);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const loadQueue = useCallback(async (tab: QueueTab) => {
    try {
      setLoading(true);
      setError(null);
      if (tab === 'aml') {
        const result = await apiService.getAmlQueue({ status: 'pending' });
        setAmlAlerts(result.alerts || []);
        setTabCounts((prev) => ({ ...prev, aml: result.total ?? (result.alerts || []).length }));
      } else {
        let result: { submissions: KYCQueueRow[]; total: number };
        if (tab === 'pending') {
          result = await apiService.getPendingKYC();
        } else if (tab === 'manual-review') {
          result = await apiService.getManualReviewKYC();
        } else {
          setSuspendedUnavailable(false);
          result = await apiService.getSuspendedKYC();
        }
        setRows(result.submissions || []);
        setTabCounts((prev) => ({
          ...prev,
          [tab]: result.total ?? (result.submissions || []).length,
        }));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load queue';
      if (tab === 'suspended' && message === 'SUSPENDED_QUEUE_UNAVAILABLE') {
        setSuspendedUnavailable(true);
        setRows([]);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue(activeTab);
  }, [activeTab, loadQueue]);

  const openDetails = async (row: KYCQueueRow) => {
    setSelectedRow(row);
    setShowDetails(true);
    setKycDetails(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const details = await apiService.getKYCDetails(row.userId);
      setKycDetails(details);
    } catch (err: unknown) {
      setDetailError(err instanceof Error ? err.message : 'Failed to load KYC details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedRow(null);
    setKycDetails(null);
    setDetailError(null);
  };

  /** Refresh the active queue and, if the detail modal is open for this user, its data too. */
  const afterAction = async (userId: string) => {
    await loadQueue(activeTab);
    if (showDetails && selectedRow?.userId === userId) {
      try {
        const details = await apiService.getKYCDetails(userId);
        setKycDetails(details);
      } catch {
        // Detail refresh is best-effort; the queue view is already up to date.
      }
    }
  };

  // ── Confirm-only actions (approve / reset) ────────────────────────────────
  const openConfirm = (action: ConfirmAction, userId: string) => {
    setActionUserId(userId);
    setConfirmAction(action);
  };

  const executeConfirmAction = async () => {
    if (!confirmAction || !actionUserId) return;
    const userId = actionUserId;
    const action = confirmAction;
    try {
      setActionLoading(userId);
      if (action === 'approve') {
        await apiService.approveKYC(userId);
      } else {
        await apiService.resetKYC(userId);
      }
      setConfirmAction(null);
      setActionUserId(null);
      await afterAction(userId);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : `Failed to ${action} KYC`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Reason-based actions (reject / request-documents / suspend / unsuspend)
  const openReasonModal = (action: ReasonAction, userId: string) => {
    setActionUserId(userId);
    setReasonText('');
    setReasonAction(action);
  };

  const closeReasonModal = () => {
    setReasonAction(null);
    setReasonText('');
    setActionUserId(null);
  };

  const executeReasonAction = async () => {
    if (!reasonAction || !actionUserId) return;
    const config = REASON_MODAL_CONFIG[reasonAction];
    const reason = reasonText.trim();
    if (config.required && !reason) {
      alert(`Please provide a ${config.label.toLowerCase()}`);
      return;
    }
    const userId = actionUserId;
    const action = reasonAction;
    try {
      setActionLoading(userId);
      if (action === 'reject') {
        await apiService.rejectKYC(userId, reason);
      } else if (action === 'request-documents') {
        await apiService.requestKYCDocuments(userId, reason);
      } else if (action === 'suspend') {
        await apiService.suspendKYC(userId, reason);
      } else {
        await apiService.unsuspendKYC(userId, reason || undefined);
      }
      closeReasonModal();
      await afterAction(userId);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── AML alert review ──────────────────────────────────────────────────────
  const handleAmlReview = async (alertId: string, action: 'cleared' | 'escalated' | 'freeze') => {
    try {
      setActionLoading(alertId);
      await apiService.reviewAmlAlert(alertId, action);
      await loadQueue('aml');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to review AML alert');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

  const renderRowActions = (row: KYCQueueRow) => {
    const busy = actionLoading === row.userId;
    return (
      <div className="submission-actions">
        <button className="btn-secondary" onClick={() => openDetails(row)}>
          View Details
        </button>
        {activeTab === 'suspended' ? (
          <button
            className="btn-success"
            onClick={() => openReasonModal('unsuspend', row.userId)}
            disabled={busy}
          >
            {busy ? 'Working...' : 'Unsuspend'}
          </button>
        ) : (
          <>
            <button
              className="btn-success"
              onClick={() => openConfirm('approve', row.userId)}
              disabled={busy}
            >
              {busy ? 'Working...' : 'Approve'}
            </button>
            <button
              className="btn-danger"
              onClick={() => openReasonModal('reject', row.userId)}
              disabled={busy}
            >
              Reject
            </button>
            <button
              className="btn-warning"
              onClick={() => openReasonModal('request-documents', row.userId)}
              disabled={busy}
            >
              Request Docs
            </button>
          </>
        )}
      </div>
    );
  };

  const renderKYCQueue = () => {
    if (activeTab === 'suspended' && suspendedUnavailable) {
      return (
        <div className="empty-state">
          <div className="empty-icon">⏳</div>
          <h2>Suspended Queue Not Live Yet</h2>
          <p>
            The backend endpoint <code>/admin/kyc/suspended</code> is not deployed yet. Refresh
            once the backend rollout is through.
          </p>
        </div>
      );
    }
    if (rows.length === 0) {
      const copy = TAB_EMPTY_COPY[activeTab];
      return (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h2>{copy.title}</h2>
          <p>{copy.body}</p>
        </div>
      );
    }
    return (
      <div className="submissions-list">
        {rows.map((row) => (
          <div key={row.userId} className="submission-card">
            <div className="submission-header">
              <div className="user-info">
                <div className="user-avatar">{(row.email || '?').charAt(0).toUpperCase()}</div>
                <div className="user-details">
                  <h3>{row.username}</h3>
                  <p className="user-email">{row.email}</p>
                </div>
              </div>
              <div className="submission-meta">
                <StatusChip status={row.kycStatus} />
                {row.submittedAt && (
                  <span className="submitted-date">
                    Submitted: {new Date(row.submittedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {(typeof row.twoPlusTwoMatches === 'number' || row.rejectionReason) && (
              <div className="kyc-row-extra">
                {typeof row.twoPlusTwoMatches === 'number' && (
                  <span>
                    <strong>2+2 Check:</strong> {row.twoPlusTwoMatches}/4 matches
                  </span>
                )}
                {row.rejectionReason && (
                  <span>
                    <strong>Reason:</strong> {row.rejectionReason}
                  </span>
                )}
              </div>
            )}

            {renderRowActions(row)}

            {row.sumsubApplicantId && (
              <div className="sumsub-info">
                <span className="sumsub-label">Sumsub Applicant ID:</span>
                <span className="sumsub-id">{row.sumsubApplicantId}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderAmlQueue = () => {
    if (amlAlerts.length === 0) {
      const copy = TAB_EMPTY_COPY.aml;
      return (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h2>{copy.title}</h2>
          <p>{copy.body}</p>
        </div>
      );
    }
    return (
      <div className="kyc-table-wrap">
        <table className="kyc-aml-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Risk</th>
              <th>Trigger</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {amlAlerts.map((alert) => {
              const busy = actionLoading === alert.id;
              return (
                <tr key={alert.id}>
                  <td>
                    <div className="aml-user-cell">
                      <strong>{alert.username || alert.userEmail || '—'}</strong>
                      {alert.userEmail && <span>{alert.userEmail}</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`risk-chip ${alert.riskLevel || 'low'}`}>
                      {alert.riskLevel || '—'}
                    </span>
                  </td>
                  <td>{alert.triggerType || '—'}</td>
                  <td>{formatDate(alert.createdAt)}</td>
                  <td>
                    <div className="aml-actions">
                      <button
                        className="btn-secondary"
                        disabled={busy}
                        onClick={() => handleAmlReview(alert.id, 'cleared')}
                      >
                        Clear
                      </button>
                      <button
                        className="btn-warning"
                        disabled={busy}
                        onClick={() => handleAmlReview(alert.id, 'escalated')}
                      >
                        Escalate
                      </button>
                      <button
                        className="btn-danger"
                        disabled={busy}
                        onClick={() => handleAmlReview(alert.id, 'freeze')}
                      >
                        Freeze
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDetailActions = () => {
    if (!selectedRow || !kycDetails) return null;
    const status = kycDetails.kycStatus.status;
    const userId = selectedRow.userId;
    const busy = actionLoading === userId;
    return (
      <div className="kyc-details-section">
        <h3>Override Actions</h3>
        <div className="kyc-detail-actions">
          {status === 'suspended' ? (
            <button
              className="btn-success"
              disabled={busy}
              onClick={() => openReasonModal('unsuspend', userId)}
            >
              Unsuspend
            </button>
          ) : (
            <>
              {status !== 'approved' && (
                <button
                  className="btn-success"
                  disabled={busy}
                  onClick={() => openConfirm('approve', userId)}
                >
                  Approve
                </button>
              )}
              {status !== 'rejected' && (
                <button
                  className="btn-danger"
                  disabled={busy}
                  onClick={() => openReasonModal('reject', userId)}
                >
                  Reject
                </button>
              )}
              <button
                className="btn-warning"
                disabled={busy}
                onClick={() => openReasonModal('request-documents', userId)}
              >
                Request Documents
              </button>
              <button
                className="btn-danger"
                disabled={busy}
                onClick={() => openReasonModal('suspend', userId)}
              >
                Suspend
              </button>
            </>
          )}
          <button
            className="btn-secondary"
            disabled={busy}
            onClick={() => openConfirm('reset', userId)}
          >
            Reset KYC
          </button>
        </div>
      </div>
    );
  };

  const reasonConfig = reasonAction ? REASON_MODAL_CONFIG[reasonAction] : null;
  const confirmConfig = confirmAction ? CONFIRM_CONFIG[confirmAction] : null;

  return (
    <div className="pending-kyc-page">
      <div className="page-header kyc-console-header">
        <div>
          <h1>KYC Console</h1>
          <p className="page-subtitle">
            Review verification queues and apply override actions
          </p>
        </div>
        <button className="btn-secondary" onClick={() => loadQueue(activeTab)} disabled={loading}>
          ↻ Refresh
        </button>
      </div>

      <div className="kyc-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`kyc-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tabCounts[tab.id] !== undefined && (
              <span className="kyc-tab-count">{tabCounts[tab.id]}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading {TABS.find((t) => t.id === activeTab)?.label}…</div>
      ) : error ? null : activeTab === 'aml' ? (
        renderAmlQueue()
      ) : (
        renderKYCQueue()
      )}

      {/* KYC Details Modal */}
      {showDetails && selectedRow && (
        <div className="modal-overlay" onClick={closeDetails}>
          <div className="modal-content kyc-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>KYC Details</h2>
                <p className="kyc-detail-user">
                  {selectedRow.username} · {selectedRow.email}
                </p>
              </div>
              <button className="modal-close" onClick={closeDetails}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {detailLoading && <div className="loading">Loading KYC details…</div>}
              {detailError && <div className="error-message">{detailError}</div>}

              {kycDetails && (
                <>
                  <div className="kyc-details-section">
                    <h3>Current Status</h3>
                    <p>
                      <strong>Status:</strong> <StatusChip status={kycDetails.kycStatus.status} />
                    </p>
                    <p>
                      <strong>User ID:</strong> <span className="sumsub-id">{selectedRow.userId}</span>
                    </p>
                    {kycDetails.kycStatus.sumsubApplicantId && (
                      <p>
                        <strong>Sumsub Applicant:</strong>{' '}
                        <span className="sumsub-id">{kycDetails.kycStatus.sumsubApplicantId}</span>
                      </p>
                    )}
                    <p>
                      <strong>Submitted:</strong> {formatDate(kycDetails.kycStatus.submittedAt)}
                    </p>
                    {kycDetails.kycStatus.approvedAt && (
                      <p>
                        <strong>Approved:</strong> {formatDate(kycDetails.kycStatus.approvedAt)}
                        {kycDetails.kycStatus.approvedBy && (
                          <> · by {kycDetails.kycStatus.approvedBy}</>
                        )}
                      </p>
                    )}
                    {typeof selectedRow.twoPlusTwoMatches === 'number' && (
                      <p>
                        <strong>2+2 Check:</strong> {selectedRow.twoPlusTwoMatches}/4 matches
                      </p>
                    )}
                    {kycDetails.kycStatus.rejectionReason && (
                      <p>
                        <strong>Rejection Reason:</strong> {kycDetails.kycStatus.rejectionReason}
                      </p>
                    )}
                    {kycDetails.kycStatus.resubmissionRequiredReason && (
                      <p>
                        <strong>Required Documents:</strong>{' '}
                        {kycDetails.kycStatus.resubmissionRequiredReason}
                      </p>
                    )}
                  </div>

                  {renderDetailActions()}

                  <div className="kyc-details-section">
                    <h3>KYC Documents</h3>
                    {kycDetails.images.faceImageUrl ||
                    kycDetails.images.idFrontImageUrl ||
                    kycDetails.images.idBackImageUrl ? (
                      <div className="kyc-images-grid">
                        {kycDetails.images.faceImageUrl && (
                          <AuthedKYCImage path={kycDetails.images.faceImageUrl} label="Face Photo" />
                        )}
                        {kycDetails.images.idFrontImageUrl && (
                          <AuthedKYCImage path={kycDetails.images.idFrontImageUrl} label="ID Front" />
                        )}
                        {kycDetails.images.idBackImageUrl && (
                          <AuthedKYCImage path={kycDetails.images.idBackImageUrl} label="ID Back" />
                        )}
                      </div>
                    ) : (
                      <p>No documents available</p>
                    )}
                  </div>

                  <div className="kyc-details-section">
                    <h3>Submission History</h3>
                    {kycDetails.submissions.length === 0 ? (
                      <p>No submission history available</p>
                    ) : (
                      <div className="submissions-history">
                        {kycDetails.submissions.map((sub) => (
                          <div key={sub.id} className="history-item">
                            <p>
                              <strong>{sub.status}</strong> - {sub.reviewStatus || 'N/A'}
                            </p>
                            {sub.adminAction && (
                              <p>
                                <strong>Admin Action:</strong> {sub.adminAction}
                              </p>
                            )}
                            {sub.adminNotes && (
                              <p>
                                <strong>Notes:</strong> {sub.adminNotes}
                              </p>
                            )}
                            <p className="history-date">{formatDate(sub.createdAt)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal (reject / request documents / suspend / unsuspend) */}
      {reasonAction && reasonConfig && (
        <div className="modal-overlay" onClick={closeReasonModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{reasonConfig.title}</h2>
              <button className="modal-close" onClick={closeReasonModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <label>
                {reasonConfig.label}{' '}
                {reasonConfig.required && <span className="required">*</span>}
              </label>
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={reasonConfig.placeholder}
                rows={4}
              />
              <div className="modal-actions">
                <button className="btn-secondary" onClick={closeReasonModal}>
                  Cancel
                </button>
                <button
                  className={reasonConfig.confirmClass}
                  onClick={executeReasonAction}
                  disabled={
                    (reasonConfig.required && !reasonText.trim()) || actionLoading !== null
                  }
                >
                  {actionLoading !== null ? reasonConfig.loadingText : reasonConfig.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal (approve / reset) */}
      {confirmConfig && (
        <ConfirmModal
          isOpen={confirmAction !== null}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Cancel"
          onConfirm={executeConfirmAction}
          onCancel={() => {
            setConfirmAction(null);
            setActionUserId(null);
          }}
          confirmButtonClass={confirmConfig.buttonClass}
        />
      )}
    </div>
  );
};

export default PendingKYCPage;
